/**
 * Product master API client (S1.4 — legacy /product-taxonomy endpoints
 * removed with the codex pages; the 5-capable tree lives in taxonomy.api.ts)
 *
 * CU-1b (the PRODUCTS third of the CU-1 flip): the SIX methods with a graph
 * root gain a live-module leg (the `products` key, client.ts) executing the
 * contract operations against `/graphql` — `list`, `getById`, `create`,
 * `update`, `setActive`, `checkSku`. SIGNATURES ARE UNCHANGED — consumers
 * cannot tell which leg served them, which is the flip's whole claim and what
 * the L2 fe-seam shadow leg byte-diffs.
 *
 * Six live + seven named holes + ONE DELEGATING ALIAS =
 * the fourteen methods on this facade. The alias is `deactivate`, now literally
 * `setActive(id, false)`: D-3 (2026-08-10) turned the sixth live leg from a
 * verb into a setter, because a verb that hardcoded `isActive: false` left
 * REACTIVATE with nowhere to go and both consumer sites sent it through
 * `update`, where the wire silently dropped it. An alias owns no leg of its
 * own, so it is neither live nor a hole — it is counted separately rather than
 * folded into a number that would then be wrong in a third way.
 *
 * The count used to read "seven" live; it was never seven. Every one of these
 * numbers is pinned to the source by `productNamedHoles.test.ts` rather than to
 * this sentence — counting the flip checks in the file BODY, since the one in
 * `vendorLineWriteReachesTheRead` is a capability predicate, not a leg, and
 * deriving the alias set from the delegation syntax rather than from a list.
 *
 * Refusals are rebuilt into the mock's own axios-shaped `{error, message}` +
 * status (LegacyProductError), including the 409 `conflict` body the wizard's
 * dup-check FSM renders, so no consumer learns a second error shape.
 *
 * SEVEN METHODS STAY ON THE MOCK, and they are NAMED rather than quietly
 * half-flipped (register §0g). Every one of them is REACHED THROUGH THIS
 * FACADE — that is the point of listing them here. A product-surface call
 * made directly on `apiClient` is not a "known hole", it is an unknown one:
 * it never consults `isLiveModule`, it never appears in this enumeration, and
 * the browser-proof driver classifies it into a bucket that is structurally
 * incapable of failing the zero-legacy-REST gate. The rule is not about hooks.
 * A COMPONENT is one directory further from this file, not one step closer to
 * compliance — round 3 found the two worst offenders there, below every guard
 * the wave had built.
 *
 * Every call caught doing it has been routed here to be counted: `salesStats`
 * and `scnGroupSpec` at 2361c96, `checkOem` and `setVendorLineActive` at the
 * round-4 pass, and `ScnSpecDetailPage` onto the `scnGroupSpec` that already
 * existed. What CANNOT be routed here — a products-SURFACE call whose endpoint
 * this facade does not own (goods receipts, AR invoices, the NetSuite
 * availability read-through, the SCN collection) — is registered in §0g-bis,
 * `productSurfaceTransportRegister.ts`. Neither register is trusted to be
 * complete: `productSurfaceCensus.test.ts` COMPUTES the call-site set from the
 * router and the import graph and fails on anything neither of them holds.
 * That is the difference between this header and the four rounds of panel
 * findings that preceded it — a list is only as complete as its last reader.
 *
 * The seven:
 *
 *   · `checkOem` — the ADVISORY OEM dup-check the wizard runs beside the SKU
 *     dup-guard. `checkSku` has a live leg (`productSkuAvailability`);
 *     `checkOem` has NO root. The near-miss to refuse: `products(filter:)`
 *     exists and takes a `search` string documented as a "sku /
 *     internalProductId / name / OEM / MPN / SCN contains-match". That is a
 *     CONTAINS match over six fields with no category scoping — the board's
 *     criterion is BOTH「同產品大類」AND「同OEM NUMBER」 exactly, plus a
 *     `sameMarket` flag off the subsidiary. Building the check on that filter
 *     would return neighbours it must not return and miss nothing only by
 *     luck: a LOSSY READ WEARING AN UNCHANGED SIGNATURE, the same shape the
 *     `salesStats` entry below refuses. The rule itself already lives once, in
 *     `packages/domain/src/rules/product.ts` (`findOemMatches`), so a root can
 *     import it rather than restate it.
 *     REMOVAL TRIGGER: a root that answers the two-criteria key — e.g.
 *     `productOemMatches(oemNo, categoryId, subsidiaryId, excludeId)` — with
 *     the server resolving it through domain `findOemMatches`.
 *   · `addVendor` / `setPreferredVendor` / `setVendorLineActive` —
 *     the product vendor LINES have no mutation root at all (measured on the
 *     live SDL 2026-08-10: 63 mutation roots, zero
 *     `productVendorLine*`). `preferredVendor` is recorded as "(dead)" in the
 *     plan's own module list, and inventing roots for a surface nobody has
 *     specified would be worse than leaving them.
 *     THE THREE WRITES REFUSE. The vendor-line READ is graph-served, so with
 *     `products` flipped a write to the mock is unobservable — and because the
 *     Postgres seed derives from the SAME fixture the mock handlers gate on, it
 *     comes back 200/201 for every seeded product rather than 404. A hole that
 *     answers success is not a hole a caller can detect, so the facade refuses
 *     it and the UI hides the control: `vendorLineWriteReachesTheRead()` is one
 *     predicate read by both, so control and transport cannot disagree.
 *     THE GROUP IS NOW ALL WRITES. `listVendors` was its fourth member and its
 *     only READ; it was DELETED 2026-08-10 together with its sole caller, the
 *     never-wired `useProductVendors` hook. It was kept while that hook existed
 *     on the argument that a consumer might appear and make it a READ/READ
 *     split — but this register's membership rule is REACHED THROUGH THIS
 *     FACADE, and a method nothing calls emits nothing, buckets into nothing,
 *     and can neither pass nor fail the zero-legacy-REST gate. It was not a
 *     hole in the surface; it was unreachable code holding a register slot open
 *     while implying a REST read path for vendor lines that no page takes.
 *     Every rendered line comes from `product.vendorLines`, NESTED on the live
 *     `ProductWireDetail`. If a vendor-line read is ever needed it comes back
 *     HERE, with a row in this register — not as an `apiClient` call in a hook,
 *     which `productSurfaceCensus.test.ts` fails by construction whether or not
 *     anyone remembered to anticipate it.
 *     REMOVAL TRIGGER: a `productVendorLine*` mutation root on the SDL.
 *   · `vendorPrefill` — a purchase-order read model (B3' scope), not a
 *     product one. REMOVAL TRIGGER: B3' lands the PO read model.
 *   · `salesStats` — a live `productSalesStats` root EXISTS, and it is a
 *     DELIBERATE WAIT_API STUB: `server/src/modules/product/graph.ts:1439-1450`
 *     returns `{productId, sourceStatus: SALES_HISTORY_UNAVAILABLE, asOf}` and
 *     nothing else, because the sales-order tables land in B3'. The consumer
 *     (`SalesAnalyticsSection`, SR3.3) renders ten substantive fields —
 *     qtySold30/90/365, avgDailyDemand, avgUnitPrice, avgMarginPct, monthly[],
 *     historyDays, nonZeroMonths. Binding a live leg to that root would empty
 *     the 銷售分析 section: a LOSSY READ WEARING AN UNCHANGED SIGNATURE, which
 *     is the exact shape this wave twice refused to merge. Root-exists is not
 *     root-serves. REMOVAL TRIGGER: `ProductSalesStats` on the SDL carries the
 *     ten fields AND `sourceStatus` can return `DERIVED`.
 *   · `scnGroupSpec` — no root at all. `scnGroups` / `scnGroup(scn)` are NOT
 *     on the SDL; `yarn validate:operations` reports `ScnSpecListPage.graphql`
 *     and `ScnSpecDetailPage.graphql` as ahead-of-schema (not yet built), and
 *     `ScnSpecGroup` / `ScnGroupFilterInput` resolve to nothing in
 *     `packages/contracts/schema.graphql`. The SDL says the full Spec Master
 *     read model is B2.4b' scope; B2.4b' landed without it.
 *     REMOVAL TRIGGER: a `scnGroup(scn)` root on the SDL.
 *
 * The wizard calls `addVendor` AFTER `create`, so in the flipped posture a
 * created product persists live and its vendor lines are still mock-side.
 * That is a real seam and it is written down here, not discovered later.
 * The vendor-line READ is live (nested on `ProductWireDetail`), so it is a
 * WRITE/READ SPLIT, not "lines stay mock-side": a line added through the
 * wizard lands in the mock and can never appear on a Postgres-served detail
 * page. `productNamedHoles.test.ts` demonstrates that split rather than
 * asserting it.
 */

import apiClient from './axios.config';
import { isLiveModule } from './client';
import { gqlMutate, gqlRequest } from './graphql.client';
import { API_ROUTES } from '@/constants/api.constants';
import {
  productOperations,
  type ProductSkuAvailabilityResult,
  type ProductWireCreateResult,
  type ProductWireDetailResult,
  type ProductWireListResult,
  type ProductWireListVariables,
  type ProductWireSetActiveResult,
  type ProductWireUpdateResult,
} from './product.operations';
import {
  mapProductWire,
  mapSkuConflict,
  throwProductGuard,
  throwProductNotFound,
} from './product.gql-map';
import type {
  CheckOemParams,
  CheckOemResponse,
  CheckSkuParams,
  CheckSkuResponse,
  CreateProductRequest,
  Product,
  ProductDetailResponse,
  ProductListParams,
  ProductListResponse,
  ProductSalesStatsResponse,
  ProductVendorLine,
  SetPreferredVendorRequest,
  UpdateProductRequest,
  UpdateProductVendorLineRequest,
} from '@/types/api/product';
import type { VendorPrefillResponse } from '@/types/api/purchaseOrder';
import type { ScnGroupDetailResponse } from '@/types/api/scn';

/** Only keys the caller actually sent reach the graph input (PATCH `??`). */
function definedOnly<T extends Record<string, unknown>>(input: T): T {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)) as T;
}

/**
 * Can ANY vendor-line write be SEEN by the next read?
 *
 * The vendor-line read and write sit on different seams whenever `products` is
 * flipped: the read is nested on `ProductWireDetail` (Postgres), and NO
 * vendor-line write has a mutation root — measured on the live SDL 2026-08-10,
 * 63 mutation roots, zero `productVendorLine*` — so every one of them falls to
 * MSW. A write that cannot be read back is not a degraded write, it is a no-op
 * with a success toast, so the surface asks this FIRST and hides the control
 * instead of asking forgiveness afterwards.
 *
 * ONE PREDICATE FOR THE WHOLE GROUP, and that is the correction of 2026-08-10.
 * It was called `vendorLineStatusWriteReachesTheRead` and only `setVendorLineActive`
 * consulted it, on the written-down belief that the other three were different.
 * They were not: `addVendor` and `setPreferredVendor` POSTed to the same absent
 * root and fired `message.success` over the same unmoved Postgres row. Nothing
 * about the STATUS field was ever the reason — the reason is the group's
 * missing root, so the fact is named for the group.
 *
 * AND THE FAILURE IS SILENT, NOT A 404. The MSW handlers gate on the product
 * existing in the mock corpus (`mocks/handlers/product.ts:567`, `:636`), and
 * `server/src/modules/product/persistence.seed.ts:542` seeds Postgres from the
 * SAME fixture — so for every seeded product the mock answers 200/201 and the
 * toast is a fiction. Only a Postgres-only product 404s into an honest error.
 * That is why this has to be a REFUSAL and not a "the error will surface it":
 * for the seeded majority there is no error to surface. Named hazard H-1 in
 * `senditship-fe-be-drift-register-v2-2026-08-08.md` §0g-quater.
 *
 * Exported because the UI must not reach for `isLiveModule` itself: no
 * component in this codebase knows about transport, and this is the seam fact
 * they are allowed to know, stated as a capability rather than as a posture.
 */
export function vendorLineWriteReachesTheRead(): boolean {
  return !isLiveModule('products');
}

/** Thrown rather than performing an unobservable write. Never rendered raw. */
export class VendorLineWriteUnreachableError extends Error {
  constructor() {
    super(
      'no vendor-line write has a graph root while the vendor-line read is graph-served'
    );
    this.name = 'VendorLineWriteUnreachableError';
  }
}

/**
 * Legacy mm → ADR-008 canonical cm, as a DECIMAL STRING.
 *
 * The inverse of `cmToMm`, and the same discipline the seed boundary keeps:
 * mm/10 at `NUMERIC(7,1)` is an exact one-place shift, so it is done on the
 * digits rather than through a float — 221 mm must be "22.1", never
 * "22.099999999999998". Over-scale input is left to the scalar to REJECT
 * (reject-not-round); this function never rounds silently.
 */
export function mmToCmString(value: number | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  const negative = value < 0;
  const digits = Math.abs(value).toString();
  const [int, frac = ''] = digits.split('.');
  const padded = int.padStart(2, '0');
  const whole = padded.slice(0, padded.length - 1).replace(/^0+(?=\d)/, '');
  const tenth = padded.slice(padded.length - 1);
  return `${negative ? '-' : ''}${whole || '0'}.${tenth}${frac}`;
}

/** A weight in its ENTRY unit → canonical kg, as a decimal string (ADR-008). */
export function toCanonicalKgString(
  value: number | null | undefined,
  unit: string | null | undefined
): string | undefined {
  if (value === null || value === undefined) return undefined;
  if ((unit ?? 'kg') === 'g') {
    // g → kg is an exact three-place shift; done on the integer to keep the
    // decimal-string discipline the Weight scalar asserts.
    return (value / 1000).toFixed(3);
  }
  return String(value);
}

/** Money crosses the graph as a decimal STRING, never a binary float. */
function money(value: number | null | undefined): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return String(value);
}

/**
 * The legacy create/update body → the graph's MUTABLE input.
 *
 * Every satellite the wizard posts travels with it (B2.4c'-write); dropping
 * any of them here would re-open the lossy write the write slice just closed.
 *
 * WHAT MAY NOT LIVE HERE, AND WHY THE NAME IS LOAD-BEARING (D-4, 2026-08-10).
 * This mapper is shared by `create` and `update`, so a key placed here is
 * asserted to be in BOTH input types. `unitsTypeId` was here and is declared
 * only by `ProductCreateInput` — so every live update put an undeclared field
 * on a `ProductUpdateInput` and graphql variable coercion refused the entire
 * mutation (measured: HTTP 400, `Field "unitsTypeId" is not defined by type
 * "ProductUpdateInput"`). It now travels in the CREATE literal beside
 * `subsidiaryId`, its fellow create-only field. A key belongs here only if
 * both input types declare it; `productWriteFieldParity.test.ts` computes that
 * from the frozen SDL rather than trusting this sentence.
 */
function toMutableInput(body: Partial<CreateProductRequest>): Record<string, unknown> {
  return definedOnly({
    nameZh: body.nameZh,
    includeChildren: body.includeChildren,
    secondaryCategoryIds: body.secondaryCategoryIds,
    customerDescription: body.salesDescription,
    vendorDescription: body.purchaseDescription,
    customsDescription: body.customsDescription,
    purchaseOrderNote: body.purchaseOrderNote,
    damageStockNotes: body.damageStockNotes,
    itemComment: body.itemComment,
    netSuiteItemId: body.netSuiteItemId,
    oemNumber: body.oemNo,
    mpn: body.mpn,
    gtin: body.upcCode,
    variant: body.variant,
    extractYear: body.extractYear,
    manufacturer: body.manufacturer,
    countryOfManufacture: body.countryOfManufacture,
    faSubCategory: body.faSubCategory,
    ageStartYear: body.ageStartYear,
    ageEndYear: body.ageEndYear,
    isShared: body.isShared,
    lengthCm: mmToCmString(body.lengthMm),
    widthCm: mmToCmString(body.widthMm),
    heightCm: mmToCmString(body.heightMm),
    weight: toCanonicalKgString(body.weightKg, body.weightUnit),
    weightEntryUnit: body.weightUnit,
    costingMethod: body.costingMethod,
    basePrice: money(body.basePrice),
    currencyCode: body.currency,
    minQty: body.minQty,
    minQtyUnit: body.minQtyUnit,
    maxQty: body.maxQty,
    maxQtyUnit: body.maxQtyUnit,
    itemDefinedCost: money(body.itemDefinedCost),
    itemDefinedCostUnit: body.itemDefinedCostUnit,
    customerPartNumbers: body.customerPartNumbers?.map((cpn) =>
      definedOnly({
        id: cpn.id,
        customerId: cpn.customerId,
        customerName: cpn.customerName,
        partNumber: cpn.partNumber,
        description: cpn.description,
        isAuthorized: cpn.isAuthorized,
        effectiveFrom: cpn.effectiveFrom,
        effectiveTo: cpn.effectiveTo,
      })
    ),
    images: body.images?.map((image) => ({ slot: image.slot, url: image.url })),
    // The legacy wire's multi-select makes/models is the CROSS-PRODUCT of the
    // rows the graph stores — the same expansion the seeder does, so a
    // round trip through either world reconstructs the same object.
    fitment: body.fitment
      ? (body.fitment.makes.length === 0
          ? []
          : body.fitment.makes.flatMap((make) =>
              (body.fitment!.models.length === 0 ? [null] : body.fitment!.models).map((model) => ({
                make,
                model,
                variant: body.variant ?? null,
                yearRanges: (body.fitment?.years ?? []).map((year) => ({
                  fromYear: year.yearFrom,
                  toYear: year.yearTo ?? null,
                  variant: year.variant ?? null,
                })),
              }))
            ))
      : undefined,
    notes: body.productNotes?.map((note) =>
      definedOnly({ id: note.id, audience: note.audience.toUpperCase(), body: note.text })
    ),
    accountingRefs: body.accounting
      ? definedOnly({
          incomeAccountRef: body.accounting.incomeAccountId,
          cogsAccountRef: body.accounting.cogsAccountId,
          assetAccountRef: body.accounting.assetAccountId,
          deferredRevenueAccountRef: body.accounting.deferredRevenueAccountId,
          dropshipExpenseAccountRef: body.accounting.dropshipExpenseAccountId,
          gainLossAccountRef: body.accounting.gainLossAccountId,
          priceVarianceAccountRef: body.accounting.priceVarianceAccountId,
          quantityVarianceAccountRef: body.accounting.quantityVarianceAccountId,
          exchangeRateVarianceAccountRef: body.accounting.exchangeRateVarianceAccountId,
          vendorBillPriceVarianceAccountRef: body.accounting.vendorBillPriceVarianceAccountId,
          vendorBillQuantityVarianceAccountRef: body.accounting.vendorBillQuantityVarianceAccountId,
          vendorBillExchangeRateVarianceAccountRef:
            body.accounting.vendorBillExchangeRateVarianceAccountId,
          vendorReturnVarianceAccountRef: body.accounting.vendorReturnVarianceAccountId,
          customerReturnVarianceAccountRef: body.accounting.customerReturnVarianceAccountId,
          intercompanyIncomeAccountRef: body.accounting.intercompanyIncomeAccountId,
          intercompanyCogsAccountRef: body.accounting.intercompanyCogsAccountId,
          taxScheduleRef: body.accounting.taxScheduleId,
        })
      : undefined,
    replenishmentPlanning: body.planning
      ? definedOnly({
          reorderPointQty: body.planning.reorderPoint,
          preferredStockLevelQty: body.planning.preferredStockLevel,
          reorderMultipleQty: body.planning.reorderMultiple,
          leadTimeDays: body.planning.purchaseLeadTimeDays,
          safetyStockDays: body.planning.safetyStockDays,
          expectedDemandChangePct:
            body.planning.expectedDemandChangePct === null ||
            body.planning.expectedDemandChangePct === undefined
              ? undefined
              : String(body.planning.expectedDemandChangePct),
          seasonalDemand: body.planning.seasonalDemand,
          preOrder: body.planning.preOrder,
          doNotOrder: body.planning.doNotOrder,
        })
      : undefined,
  });
}

/** The legacy list filters → the graph's ProductFilterInput. */
function toFilter(params?: ProductListParams): ProductWireListVariables['filter'] {
  const filter = definedOnly({
    search: params?.search || undefined,
    categoryId: params?.categoryId || undefined,
    isActive: params?.isActive,
    hasScn: params?.hasScn,
    readiness:
      params?.readiness === 'ready'
        ? ('READY' as const)
        : params?.readiness === 'not_ready'
          ? ('NOT_READY' as const)
          : undefined,
  });
  return Object.keys(filter).length === 0 ? undefined : filter;
}

/**
 * Every row of the filtered set, page by page.
 *
 * The legacy wire is page/pageSize and the graph is Relay, so the facade
 * drains the connection and slices — the same thing the mock handler does
 * with an in-memory array, including its `pageSize <= 0` = "return every row"
 * rule (a naive slice drops the last row; the CU-1a options contract).
 */
async function drainProducts(params?: ProductListParams): Promise<Product[]> {
  const rows: Product[] = [];
  let after: string | null = null;
  // 200 keeps the round trips down without asking the server for an unbounded
  // page; the loop, not the constant, is what guarantees completeness.
  for (;;) {
    const result: ProductWireListResult = await gqlRequest<
      ProductWireListResult,
      ProductWireListVariables
    >(productOperations.wireList, { first: 200, after, filter: toFilter(params) });
    rows.push(...result.products.edges.map((edge) => mapProductWire(edge.node)));
    if (!result.products.pageInfo.hasNextPage) break;
    after = result.products.pageInfo.endCursor;
    if (after === null) break;
  }
  return rows;
}

export const productApi = {
  list: async (params?: ProductListParams): Promise<ProductListResponse> => {
    if (isLiveModule('products')) {
      let rows = await drainProducts(params);
      // The one filter with no graph equivalent: the deprecated SO-picker
      // flag, which is `isActive && ready` — composed here from fields the
      // rows already carry rather than asked of the server.
      if (params?.occReadyOnly) {
        rows = rows.filter((row) => row.isActive && row.readiness?.ready);
      }
      const page = params?.page ?? 1;
      const pageSize = params?.pageSize ?? 20;
      const total = rows.length;
      // pageSize <= 0 = EVERY row (the options contract).
      const data = pageSize <= 0 ? rows : rows.slice((page - 1) * pageSize, page * pageSize);
      return {
        data,
        pagination: {
          page,
          pageSize,
          total,
          totalPages: pageSize <= 0 ? 1 : Math.ceil(total / pageSize),
        },
      };
    }
    const { data } = await apiClient.get<ProductListResponse>(API_ROUTES.PRODUCTS, { params });
    return data;
  },

  getById: async (id: string): Promise<ProductDetailResponse> => {
    if (isLiveModule('products')) {
      const result = await gqlRequest<ProductWireDetailResult, { id: string }>(
        productOperations.wireDetail,
        { id }
      );
      if (!result.product) throwProductNotFound();
      return { data: mapProductWire(result.product) };
    }
    const { data } = await apiClient.get<ProductDetailResponse>(`${API_ROUTES.PRODUCTS}/${id}`);
    return data;
  },

  create: async (body: CreateProductRequest): Promise<ProductDetailResponse> => {
    if (isLiveModule('products')) {
      const result = await gqlMutate<ProductWireCreateResult, { input: Record<string, unknown> }>(
        productOperations.wireCreate,
        {
          input: definedOnly({
            sku: body.sku,
            displayName: body.displayName,
            // The two CREATE-ONLY fields — `ProductUpdateInput` declares
            // neither, so they live here and never in `toMutableInput`.
            subsidiaryId: body.subsidiaryId,
            unitsTypeId: body.unitsTypeId,
            categoryId: body.categoryId,
            baseUom: body.stockUnit ?? 'PCS',
            purchaseUom: body.purchaseUnit,
            salesUom: body.saleUnit,
            ...toMutableInput(body),
          }),
        }
      );
      const payload = result.productCreate;
      // The mock's 409 body carries the conflicting row; the graph reports the
      // code and the SKU, so the facade fetches the conflict to rebuild the
      // exact body the wizard's FSM reads.
      if (payload.userErrors.length > 0) {
        // The mock's 409 names the row that holds the SKU (`conflictId` + the
        // internalProductId in the message). The graph reports the code, so
        // the facade resolves the row through the check it already owns.
        const conflict =
          payload.userErrors[0].code === 'SKU_TAKEN'
            ? await productApi
                .checkSku({ sku: body.sku })
                .then((check) => check.conflict ?? null)
                .catch(() => null)
            : null;
        throwProductGuard(payload.userErrors, conflict);
      }
      if (!payload.product) throwProductNotFound();
      return { data: mapProductWire(payload.product) };
    }
    const { data } = await apiClient.post<ProductDetailResponse>(API_ROUTES.PRODUCTS, body);
    return data;
  },

  update: async (id: string, body: UpdateProductRequest): Promise<ProductDetailResponse> => {
    if (isLiveModule('products')) {
      const result = await gqlMutate<ProductWireUpdateResult, { input: Record<string, unknown> }>(
        productOperations.wireUpdate,
        {
          input: definedOnly({
            productId: id,
            sku: body.sku,
            displayName: body.displayName,
            categoryId: body.categoryId,
            baseUom: body.stockUnit,
            purchaseUom: body.purchaseUnit,
            salesUom: body.saleUnit,
            // The retroactive SCN link — the ONLY way `scn` is ever set.
            scn: body.scn,
            ...toMutableInput(body),
          }),
        }
      );
      const payload = result.productUpdate;
      if (payload.userErrors.length > 0) {
        const conflict =
          payload.userErrors[0].code === 'SKU_TAKEN' && body.sku
            ? await productApi
                .checkSku({ sku: body.sku, excludeId: id })
                .then((check) => check.conflict ?? null)
                .catch(() => null)
            : null;
        throwProductGuard(payload.userErrors, conflict);
      }
      if (!payload.product) throwProductNotFound();
      return { data: mapProductWire(payload.product) };
    }
    const { data } = await apiClient.patch<ProductDetailResponse>(
      `${API_ROUTES.PRODUCTS}/${id}`,
      body
    );
    return data;
  },

  /**
   * Soft activate / deactivate — the boolean TRAVELS.
   *
   * MEASURED REASON THIS IS A SETTER AND NOT A VERB (D-3, 2026-08-10).
   * This method used to be `deactivate`, and it hardcoded `isActive: false`
   * at the wire. That left the two consumer sites with nowhere to send a
   * REACTIVATE, so both routed it through `update({ isActive: true })` —
   * a call that cannot work on the live path and cannot fail either:
   * `ProductUpdateInput` declares no `isActive`, `toMutableInput` is an
   * ALLOWLIST that does not carry it, so `definedOnly` left `{ productId }`.
   * An EMPTY PATCH returns `userErrors: []`, the facade does not throw, and
   * both sites toast SUCCESS while Postgres keeps `is_active = false`. A
   * product deactivated in the UI could never be reactivated from the UI.
   *
   * The graph root was ALREADY a set — `ProductSetActiveInput` has carried
   * `isActive: Boolean!` all along, so nothing in the frozen SDL had to
   * change; only the facade was throwing the boolean away.
   *
   * Reproduced on the live seam and re-proved fixed (deactivate → reactivate
   * → psql) at `Docs/senditship-run-2026-08-08/
   * cu1b-d3-reactivate-repro-2026-08-10.txt`.
   */
  setActive: async (id: string, isActive: boolean): Promise<ProductDetailResponse> => {
    if (isLiveModule('products')) {
      const result = await gqlMutate<
        ProductWireSetActiveResult,
        { input: Record<string, unknown> }
      >(productOperations.wireSetActive, { input: { productId: id, isActive } });
      const payload = result.productSetActive;
      if (payload.userErrors.length > 0) throwProductGuard(payload.userErrors);
      if (!payload.product) throwProductNotFound();
      return { data: mapProductWire(payload.product) };
    }
    const { data } = await apiClient.post<ProductDetailResponse>(
      `${API_ROUTES.PRODUCTS}/${id}/set-active`,
      { isActive }
    );
    return data;
  },

  /** Thin caller — the legacy name, kept so existing callers do not move. */
  deactivate: (id: string): Promise<ProductDetailResponse> => productApi.setActive(id, false),

  /** Live SKU dup-guard (the wizard's 800ms-debounced check FSM). */
  checkSku: async (params: CheckSkuParams): Promise<CheckSkuResponse> => {
    if (isLiveModule('products')) {
      const result = await gqlRequest<
        ProductSkuAvailabilityResult,
        { sku: string; excludeProductId?: string }
      >(
        productOperations.skuAvailability,
        definedOnly({ sku: params.sku, excludeProductId: params.excludeId })
      );
      const check = result.productSkuAvailability;
      // Key presence: the mock omits `conflict` entirely when the SKU is free.
      return check.conflict
        ? { available: check.available, conflict: mapSkuConflict(check.conflict) }
        : { available: check.available };
    }
    const { data } = await apiClient.get<CheckSkuResponse>(`${API_ROUTES.PRODUCTS}/check-sku`, {
      params,
    });
    return data;
  },

  /* ---------------------------------------------------------------------
   * NAMED HOLES — no graph root exists (register §0g). Deliberately still on
   * the mock rather than half-flipped or faked.
   * ------------------------------------------------------------------- */

  /**
   * Advisory OEM dup-check — the SKU dup-guard's sibling, one field away on
   * the same form, on the other transport. NAMED HOLE: no root (see header).
   * Routed here so it is COUNTABLE. FAILS OPEN at the call site; never a gate.
   */
  checkOem: async (params: CheckOemParams): Promise<CheckOemResponse> => {
    const { data } = await apiClient.get<CheckOemResponse>(
      `${API_ROUTES.PRODUCTS}/check-oem`,
      { params: definedOnly({ ...params }) }
    );
    return data;
  },

  /**
   * US-266 新增供應商. NAMED HOLE, vendor-line group — REFUSES rather than
   * writing where the read cannot see it. See `vendorLineWriteReachesTheRead`
   * for the one reason all three writes share.
   * REMOVAL TRIGGER: shared with the group — a `productVendorLine*` root.
   */
  addVendor: async (
    productId: string,
    body: {
      vendorId: number;
      vendorName: string;
      unitCost: number;
      currency?: string;
      /** 訂購單位 (W7-B3) — display-only capture, no conversion. */
      orderUnit?: string;
      isPreferred?: boolean;
      vendorSku?: string;
    }
  ): Promise<{ data: ProductVendorLine }> => {
    if (!vendorLineWriteReachesTheRead()) {
      throw new VendorLineWriteUnreachableError();
    }
    const { data } = await apiClient.post<{ data: ProductVendorLine }>(
      `${API_ROUTES.PRODUCTS}/${productId}/vendors`,
      body
    );
    return data;
  },

  /**
   * US-266 設為主要供應商. NAMED HOLE, vendor-line group — REFUSES for the same
   * reason as its two siblings. `isPrimary` is on the ProductWireDetail
   * selection set like every other vendor-line field, so a POST to the mock
   * moves nothing a flipped detail page can render.
   * REMOVAL TRIGGER: shared with the group — a `productVendorLine*` root.
   */
  setPreferredVendor: async (
    productId: string,
    body: SetPreferredVendorRequest
  ): Promise<ProductDetailResponse> => {
    if (!vendorLineWriteReachesTheRead()) {
      throw new VendorLineWriteUnreachableError();
    }
    const { data } = await apiClient.post<ProductDetailResponse>(
      `${API_ROUTES.PRODUCTS}/${productId}/preferred-vendor`,
      body
    );
    return data;
  },

  /**
   * US-148 per-line 生效/停用. NAMED HOLE, vendor-line group — the write whose
   * lie was MEASURED first (`e2af933`), not the only one that told it.
   *
   * CORRECTED 2026-08-10: this docblock used to read "unlike its three
   * siblings this one used to lie", and that sentence was the stated reason
   * for fixing one control and leaving two. It was false. `addVendor` and
   * `setPreferredVendor` had the identical property and both fired
   * `message.success` over it; only `listVendors`, the group's read, was
   * innocent — and only because nothing calls it. The three writes now share
   * one predicate and one refusal.
   * REMOVAL TRIGGER: shared with the group — a `productVendorLine*` root.
   */
  setVendorLineActive: async (
    productId: string,
    lineId: string,
    body: UpdateProductVendorLineRequest
  ): Promise<{ data: ProductVendorLine }> => {
    if (!vendorLineWriteReachesTheRead()) {
      throw new VendorLineWriteUnreachableError();
    }
    const { data } = await apiClient.patch<{ data: ProductVendorLine }>(
      `${API_ROUTES.PRODUCTS}/${productId}/vendors/${lineId}`,
      body
    );
    return data;
  },

  vendorPrefill: async (productId: string): Promise<VendorPrefillResponse> => {
    const { data } = await apiClient.get<VendorPrefillResponse>(
      `${API_ROUTES.PRODUCTS}/${productId}/vendor-prefill`
    );
    return data;
  },

  /**
   * Per-product sales roll-up (SR2.3). NAMED HOLE — the `productSalesStats`
   * root exists but serves three fields and no numbers (see header). Routed
   * here so the read is COUNTABLE: it consults the flip like every other
   * product call and shows up in this file's enumeration instead of hiding
   * under a hook. `asOf` pins the horizon for deterministic reads.
   */
  salesStats: async (productId: string, asOf?: string): Promise<ProductSalesStatsResponse> => {
    const { data } = await apiClient.get<ProductSalesStatsResponse>(
      `${API_ROUTES.PRODUCTS}/${productId}/sales-stats`,
      asOf ? { params: { asOf } } : undefined
    );
    return data;
  },

  /**
   * Spec Master context for one SCN group (S7.1). NAMED HOLE — no root at all
   * (see header). Same reason as `salesStats` for living here: the drawer's
   * join-mode context panel and the ScnSection completeness hint were the
   * second read that rendered mock pixels inside a live-flipped posture.
   */
  scnGroupSpec: async (scn: string): Promise<ScnGroupDetailResponse> => {
    const { data } = await apiClient.get<ScnGroupDetailResponse>(
      `${API_ROUTES.SCN_GROUPS}/${scn}`
    );
    return data;
  },
};

export default productApi;
